import { z } from "zod";
import { ContentKeySchema, GIT_COMMIT_SHA_PATTERN, LibraryAttributionSchema } from "./wire.js";
/**
 * The content sources (V6 plan 13.3.3 "A second source", H.29). `content/` is the source
 * `platform`; `content/sources.yaml` lists every other one, each a pinned git checkout that
 * holds only `libraries/` and `agents/`. The gate and the publish read `content/` and every
 * source as one tree: one gate, one vocabulary (`content/taxonomy.yaml`), library keys unique
 * across all of them. CI and the image check each source out at its commit (H.30), under
 * `content/.sources/<id>/`.
 */
export const PLATFORM_SOURCE_ID = "platform";
/** The list of sources, beside `taxonomy.yaml` in `content/`. */
export const CONTENT_SOURCES_FILE = "sources.yaml";
/** Where a source is checked out: `content/.sources/<id>/`, then the entry's `path` inside it. */
export const CONTENT_SOURCES_DIR = ".sources";
/** The only entries a source may hold at its top (its `path`). */
export const CONTENT_SOURCE_ENTRIES = ["libraries", "agents"];
/** A folder inside the source's repository: relative, and never climbing out of it. */
const SourcePathSchema = z
    .string()
    .trim()
    .min(1)
    .max(300)
    .refine((value) => !value.startsWith("/") && !value.split("/").includes(".."), {
    message: "a folder inside the source's repository, relative and without ..",
});
/**
 * One source: its `id` (never `platform`, which is `content/`), the repository and the commit it
 * is pinned at (a full SHA, never a branch; the gate also refuses a mutable reference such as
 * `…@main`), the folder inside it that holds `libraries/` and `agents/` (`.` for its root), and
 * whether it is first-party or third-party content. A `third_party` source needs an
 * `attribution` whose repo is the source's own, and THIRD_PARTY_NOTICES.md needs a section for
 * it (the gate's notices rule, H.26).
 */
export const ContentSourceSchema = z
    .object({
    id: ContentKeySchema.refine((id) => id !== PLATFORM_SOURCE_ID, {
        message: `"${PLATFORM_SOURCE_ID}" is content/ itself`,
    }),
    repo: z.string().trim().max(300).url().startsWith("https://"),
    commit: z.string().regex(GIT_COMMIT_SHA_PATTERN, {
        message: "commit must be a full 40-character lowercase SHA, never a branch, a tag or HEAD",
    }),
    path: SourcePathSchema,
    origin: z.enum(["first_party", "third_party"]),
    attribution: LibraryAttributionSchema.optional(),
})
    .strict();
/** `content/sources.yaml`: `sources`, each id once. */
export const ContentSourcesSchema = z
    .object({ sources: z.array(ContentSourceSchema).max(50).default([]) })
    .strict()
    .superRefine((file, context) => {
    const seen = new Set();
    file.sources.forEach((source, index) => {
        if (seen.has(source.id)) {
            context.addIssue({
                code: "custom",
                message: `duplicate source id "${source.id}"`,
                path: ["sources", index, "id"],
            });
        }
        seen.add(source.id);
    });
});
//# sourceMappingURL=content-sources.js.map